import { createPortal } from 'react-dom'

import { Dialog, Layout, Text } from '@loadsmart/miranda-react'

import { getTotalWeight, hasItemCommodity, hasItemWeight, hasMileage } from 'core/domain/Load'
import { getCommodities, getFormattedRate } from 'core/presenters/loadPresenter'
import { formatType } from 'core/presenters/stopPresenter'
import { getTruckLabel } from 'core/presenters/truckPresenter'
import { CardInfoItem } from 'core/ui/components/card/CardInfoItem'

import { type Load } from '../domain/Load'
import { LoadStopAddress } from './LoadStopAddress'

export type TenderAcceptDialogProps = {
  load: Load
  loading?: boolean
  onCancel: () => void
  onConfirm: () => void
  open?: boolean
}

export function TenderAcceptDialog({
  load,
  loading,
  onCancel,
  onConfirm,
  open = false,
}: TenderAcceptDialogProps) {
  function handleClick(event: React.MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
  }

  function handleConfirm() {
    if (!loading) {
      onConfirm()
    }
  }

  function handleCancel() {
    if (!loading) {
      onCancel()
    }
  }

  return createPortal(
    <Dialog
      data-testid="dialog-tender-accept"
      open={open}
      onClose={handleCancel}
      onClick={handleClick}
    >
      <Dialog.Header>
        Accept tender
        <Dialog.Close onClick={handleCancel} disabled={loading} />
      </Dialog.Header>
      <Dialog.Body>
        <Layout.Stack gap="spacing-4">
          <Text>Please confirm your tender acceptance to proceed</Text>
          {load.stops.map((stop, index) => (
            <Layout.Stack
              key={`stop-${stop.city}-${stop.state}-${index}`}
              style={{ textAlign: 'left' }}
              gap="spacing-0-5"
            >
              <Text variant="body-sm">{formatType(index, load.stops)}</Text>
              <LoadStopAddress stop={stop} />
            </Layout.Stack>
          ))}
          <Layout.Group style={{ textAlign: 'left' }}>
            <CardInfoItem title="Mode" value={load.mode} style={{ flexGrow: 1 }} />
            <CardInfoItem
              title="Equipment"
              value={getTruckLabel(load.equipmentType)}
              style={{ flexGrow: 1 }}
            />
            {hasItemWeight(load) && (
              <CardInfoItem title="Weight" value={`${getTotalWeight(load)} lbs`} />
            )}
          </Layout.Group>
          {hasItemCommodity(load) && (
            <CardInfoItem
              title="Commodity"
              value={getCommodities(load)}
              style={{ textAlign: 'left' }}
            />
          )}
          <Layout.Box backgroundColor="color-background-tertiary" padding="spacing-3">
            <Layout.Group justify="flex-end">
              {hasMileage(load) && (
                <CardInfoItem
                  title="Distance"
                  value={`${load.mileage} miles`}
                  style={{ flexGrow: 1 }}
                />
              )}
              <CardInfoItem title="Rate" value={getFormattedRate(load)} />
            </Layout.Group>
          </Layout.Box>
        </Layout.Stack>
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.ActionTertiary onClick={handleCancel} loading={loading}>
          Cancel
        </Dialog.ActionTertiary>
        <Dialog.ActionPrimary onClick={handleConfirm} loading={loading}>
          Accept tender
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </Dialog>,
    document.body
  )
}
